/* ============================================================
   SC Will It Fit — fit checker engine (v2)
   ------------------------------------------------------------
   Market-aware (UK cm / US inches), model-driven with fully
   editable dimensions, and deliberately conservative:

   Verdict tiers, best to worst:
     fit    — clears the straight side-carry with margin to spare
     tight  — clears, but inside the safety margin (5 cm / 2 in).
              Handmade frames vary, so we ask them to get in touch.
     onend  — the straight carry fails but the maths says an
              on-end / leaning carry is likely. We NEVER promise
              this: the wording is "likely to fit on its end,
              contact the team to verify before delivery".
     team   — nothing passes cleanly. Not a hard no: split frames
              (loose cover builds only), leg removal and window
              routes exist, so we route to the team.

   Geometry notes:
   - Access height (from the SSOT) is the transit height with all
     removable parts off. If the customer unticks "legs removable",
     the effective access height becomes access + leg height.
   - Straight side-carry through an opening: the smaller of depth
     and effective access height passes through the door WIDTH;
     the larger must clear the door HEIGHT.
   - On-end through an opening: door height must beat the sofa's
     external width; min(depth, access) must beat the door width.
   - Lean-through (sofa longer than the door is tall): closed-form
     minimums. Feasible lean angles satisfy t <= h*cos(theta), so
     the steepest usable angle is theta* = acos(t/h), giving
       run-in needed  r* = W * t / h
       headroom needed c* = W*sin(theta*) + t*cos(theta*)
     We report both numbers and route to the team.
   - Corner turn (hall corner, stair landing): sweep theta from
     5 to 85 degrees; the longest sofa that fits at each angle is
       L(theta) = a/sin + b/cos - t/(sin*cos)
     and the WORST angle governs. Flat carry first; if that fails,
     the on-end footprint (max/min of depth & access) is swept,
     gated on ceiling height >= sofa width. Stairs never get the
     on-end path (you cannot stand a sofa on a slope).
   ============================================================ */
(function () {
  'use strict';

  /* ============ 1. SOFA DATA (from the SSOT, 2 July 2026) ============ */
  /* UK dimensions in centimetres, US in inches. Access height is the
     authoritative transit height column, not derived. */
  var MARKETS = {
    UK: {
      unit: 'cm', altUnit: 'in', toAlt: 1 / 2.54, margin: 5,
      models: [
        { key: 'coco', name: 'Coco', legsRemovable: true, legH: 8, split: true,
          sizes: [
            { key: 'armchair', label: 'Armchair', w: 90, h: 76, d: 102, access: 68 },
            { key: 'loveseat', label: 'Loveseat', w: 140, h: 76, d: 102, access: 68 },
            { key: '2-seater', label: '2 Seater', w: 167, h: 76, d: 102, access: 68 },
            { key: '2.5-seater', label: '2.5 Seater', w: 196, h: 76, d: 102, access: 68 },
            { key: '3-seater', label: '3 Seater', w: 224, h: 76, d: 102, access: 68 },
            { key: '3.5-seater', label: '3.5 Seater', w: 251, h: 76, d: 102, access: 68 },
            { key: '4-seater', label: '4 Seater', w: 279, h: 76, d: 102, access: 68 }
          ] },
        { key: 'coco-bed', name: 'Coco Sofa Bed', legsRemovable: false, legH: 6, split: true,
          sizes: [
            { key: '1.5-seater', label: '1.5 Seater', w: 160, h: 76, d: 102, access: 76 },
            { key: '2-seater', label: '2 Seater', w: 180, h: 76, d: 102, access: 76 },
            { key: '2.5-seater', label: '2.5 Seater', w: 200, h: 76, d: 102, access: 76 }
          ] },
        { key: 'barton', name: 'Barton', legsRemovable: true, legH: 6, split: true,
          sizes: [
            { key: 'loveseat', label: 'Loveseat', w: 152, h: 76, d: 120, access: 70 },
            { key: '2-seater', label: '2 Seater', w: 207, h: 76, d: 120, access: 70 },
            { key: '3-seater', label: '3 Seater', w: 234, h: 76, d: 120, access: 70 }
          ] },
        { key: 'isobel', name: 'Isobel', legsRemovable: true, legH: 8, split: true,
          sizes: [
            { key: 'armchair', label: 'Armchair', w: 88, h: 90, d: 105, access: 80 },
            { key: 'loveseat', label: 'Loveseat', w: 116, h: 90, d: 105, access: 80 },
            { key: '2-seater', label: '2 Seater', w: 140, h: 90, d: 105, access: 80 },
            { key: '2.5-seater', label: '2.5 Seater', w: 168, h: 90, d: 105, access: 80 },
            { key: '3-seater', label: '3 Seater', w: 196, h: 90, d: 105, access: 80 },
            { key: '4-seater', label: '4 Seater', w: 252, h: 90, d: 105, access: 80 }
          ] },
        { key: 'felix', name: 'Felix', legsRemovable: true, legH: 8, split: true,
          sizes: [
            { key: 'armchair', label: 'Armchair', w: 88, h: 90, d: 105, access: 80 },
            { key: 'loveseat', label: 'Loveseat', w: 116, h: 90, d: 105, access: 80 },
            { key: '2-seater', label: '2 Seater', w: 140, h: 90, d: 105, access: 80 },
            { key: '2.5-seater', label: '2.5 Seater', w: 168, h: 90, d: 105, access: 80 },
            { key: '3-seater', label: '3 Seater', w: 196, h: 90, d: 105, access: 80 },
            { key: '4-seater', label: '4 Seater', w: 252, h: 90, d: 105, access: 80 }
          ] },
        { key: 'henrietta', name: 'Henrietta', legsRemovable: false, legH: 21, split: false,
          sizes: [
            { key: 'armchair', label: 'Armchair', w: 105, h: 92, d: 94, access: 92 },
            { key: '3-seater', label: '3 Seater', w: 210, h: 92, d: 94, access: 92 }
          ] },
        { key: 'sophia', name: 'Sophia', legsRemovable: false, legH: 29, split: true,
          sizes: [
            { key: 'armchair', label: 'Armchair', w: 73, h: 89, d: 82, access: 89 },
            { key: '2-seater', label: '2 Seater', w: 134, h: 89, d: 82, access: 89 },
            { key: '3-seater', label: '3 Seater', w: 194, h: 89, d: 82, access: 89 }
          ] }
      ],
      doorPresets: {
        front: [
          ['83,198', 'Standard UK front door (83 \u00D7 198 cm)'],
          ['76,198', 'Narrower / internal-style door (76 \u00D7 198 cm)'],
          ['68,198', 'Narrow / Victorian terrace (68 \u00D7 198 cm)'],
          ['95,210', 'Wide / double-leaf door (95 \u00D7 210 cm)'],
          ['110,220', 'Generous / period property (110 \u00D7 220 cm)']
        ],
        hall: [
          ['90,240', 'Narrow Victorian hallway (90 cm, standard ceiling)'],
          ['110,240', 'Standard hallway (110 cm)'],
          ['140,250', 'Generous hallway (140 cm+)'],
          ['0,240', 'No hallway / direct into room']
        ],
        stair: [
          ['85,220,100', 'Standard UK staircase (85 cm wide)'],
          ['80,210,90', 'Narrow / period staircase (80 cm wide)'],
          ['95,240,120', 'Generous staircase (95 cm+ wide)']
        ],
        room: [
          ['76,198', 'Standard internal door (76 \u00D7 198 cm)'],
          ['68,198', 'Narrow internal door (68 \u00D7 198 cm)'],
          ['86,210', 'Wide internal door (86 \u00D7 210 cm)'],
          ['0,0', 'Open plan / no door']
        ]
      }
    },
    US: {
      unit: 'in', altUnit: 'cm', toAlt: 2.54, margin: 2,
      models: [
        { key: 'coco', name: 'Coco', legsRemovable: true, legH: 3, split: true,
          sizes: [
            { key: 'armchair', label: 'Armchair', w: 35, h: 30, d: 37, access: 27 },
            { key: 'loveseat', label: 'Loveseat', w: 55, h: 30, d: 40, access: 27 },
            { key: '2-seater', label: '2 Seater', w: 66, h: 30, d: 40, access: 27 },
            { key: '2.5-seater', label: '2.5 Seater', w: 77, h: 30, d: 40, access: 27 },
            { key: '3-seater', label: '3 Seater', w: 88, h: 30, d: 40, access: 27 },
            { key: '3.5-seater', label: '3.5 Seater', w: 99, h: 30, d: 40, access: 27 },
            { key: '4-seater', label: '4 Seater', w: 110, h: 30, d: 40, access: 27 }
          ] },
        { key: 'coco-bed', name: 'Coco Sofa Bed', legsRemovable: false, legH: 3, split: false,
          sizes: [
            { key: '2.5-seater', label: '2.5 Seater', w: 79, h: 30, d: 40, access: 30 }
          ] },
        { key: 'barton', name: 'Barton', legsRemovable: true, legH: 2, split: true,
          sizes: [
            { key: 'loveseat', label: 'Loveseat', w: 60, h: 30, d: 47, access: 28 },
            { key: '2-seater', label: '2 Seater', w: 81, h: 30, d: 47, access: 28 },
            { key: '3-seater', label: '3 Seater', w: 92, h: 30, d: 47, access: 28 }
          ] },
        { key: 'isobel', name: 'Isobel', legsRemovable: true, legH: 4, split: true,
          sizes: [
            { key: 'armchair', label: 'Armchair', w: 30, h: 33, d: 42, access: 31 },
            { key: 'loveseat', label: 'Loveseat', w: 43, h: 33, d: 42, access: 31 },
            { key: '2-seater', label: '2 Seater', w: 55, h: 33, d: 42, access: 31 },
            { key: '2.5-seater', label: '2.5 Seater', w: 67, h: 33, d: 42, access: 31 },
            { key: '3-seater', label: '3 Seater', w: 77, h: 33, d: 42, access: 31 },
            { key: '4-seater', label: '4 Seater', w: 90, h: 33, d: 42, access: 31 }
          ] },
        { key: 'felix', name: 'Felix', legsRemovable: true, legH: 4, split: true,
          sizes: [
            { key: 'armchair', label: 'Armchair', w: 30, h: 33, d: 39, access: 31 },
            { key: 'loveseat', label: 'Loveseat', w: 43, h: 33, d: 39, access: 31 },
            { key: '2-seater', label: '2 Seater', w: 55, h: 33, d: 39, access: 31 },
            { key: '2.5-seater', label: '2.5 Seater', w: 67, h: 33, d: 39, access: 31 },
            { key: '3-seater', label: '3 Seater', w: 77, h: 33, d: 39, access: 31 },
            { key: '4-seater', label: '4 Seater', w: 90, h: 33, d: 39, access: 31 }
          ] },
        { key: 'henrietta', name: 'Henrietta', legsRemovable: false, legH: 8, split: false,
          sizes: [
            { key: 'armchair', label: 'Armchair', w: 41, h: 36, d: 37, access: 36 },
            { key: '3-seater', label: '3 Seater', w: 83, h: 36, d: 37, access: 36 }
          ] },
        { key: 'sophia', name: 'Sophia', legsRemovable: false, legH: 11, split: true,
          sizes: [
            { key: 'armchair', label: 'Armchair', w: 29, h: 35, d: 32, access: 35 },
            { key: '2-seater', label: '2 Seater', w: 53, h: 35, d: 32, access: 35 },
            { key: '3-seater', label: '3 Seater', w: 76, h: 35, d: 32, access: 35 }
          ] }
      ],
      doorPresets: {
        front: [
          ['36,80', 'Standard US front door (36 \u00D7 80 in)'],
          ['32,80', 'Narrower front door (32 \u00D7 80 in)'],
          ['30,80', 'Narrow door (30 \u00D7 80 in)'],
          ['42,84', 'Wide / double-leaf door (42 \u00D7 84 in)'],
          ['60,84', 'Generous double door (60 \u00D7 84 in)']
        ],
        hall: [
          ['36,96', 'Narrow hallway (36 in, standard ceiling)'],
          ['42,96', 'Standard hallway (42 in)'],
          ['54,100', 'Generous hallway (54 in+)'],
          ['0,96', 'No hallway / direct into room']
        ],
        stair: [
          ['36,84,40', 'Standard staircase (36 in wide)'],
          ['32,80,36', 'Narrow staircase (32 in wide)'],
          ['40,96,48', 'Generous staircase (40 in+ wide)']
        ],
        room: [
          ['30,80', 'Standard interior door (30 \u00D7 80 in)'],
          ['28,80', 'Narrow interior door (28 \u00D7 80 in)'],
          ['32,80', 'Wide interior door (32 \u00D7 80 in)'],
          ['0,0', 'Open plan / no door']
        ]
      }
    }
  };

  /* ============ 2. MARKET + STATE ============ */
  function detectMarket() {
    /* Theme convention: explicit data-sc-market attribute only; never
       inferred from browser country. Anything other than 'US' is UK. */
    var el = document.querySelector('[data-sc-market]');
    return (el && el.getAttribute('data-sc-market') === 'US') ? 'US' : 'UK';
  }

  var MARKET_KEY = detectMarket();
  var MARKET = MARKETS[MARKET_KEY];
  var MARGIN = MARKET.margin;
  var UNIT = MARKET.unit;

  var STATE = {
    modelKey: null,      /* 'coco' | ... | 'custom' */
    sizeKey: null,
    dims: { w: null, h: null, d: null, access: null, legH: null },
    legsRemovable: false,
    split: false,
    inputUnit: UNIT,     /* what the route inputs are typed in */
    stairs: false,
    corner: false
  };

  function getModel(key) {
    for (var i = 0; i < MARKET.models.length; i++) {
      if (MARKET.models[i].key === key) return MARKET.models[i];
    }
    return null;
  }

  /* Effective access height: if legs cannot come off (either by model
     or because the customer unticked it), the legs travel too. */
  function effectiveAccess() {
    var a = STATE.dims.access;
    /* Unknown access height (custom sofa): fall back to the external
       height. Conservative — never assumes anything comes off. */
    if (a == null) a = STATE.dims.h;
    if (a == null) return null;
    if (!STATE.legsRemovable && getModel(STATE.modelKey) && getModel(STATE.modelKey).legsRemovable) {
      /* Model normally loses its legs but the customer said no:
         put the leg height back on. */
      a += (STATE.dims.legH || 0);
    }
    return a;
  }

  /* ============ 3. STEP 1 — sofa grid, sizes, dims editor ============ */
  var grid = document.getElementById('sc-fc-sofa-grid');
  var sizeRow = document.getElementById('sc-fc-size-row');
  var sizeChips = document.getElementById('sc-fc-size-chips');
  var dimsBox = document.getElementById('sc-fc-dims');
  var toStep2 = document.getElementById('sc-fc-to-2');

  function renderGrid() {
    if (!grid) return;
    var html = '';
    MARKET.models.forEach(function (m) {
      html += '<button type="button" class="sc-fc-sofa-tile" data-model="' + m.key + '">' +
        '<span class="sc-fc-tile-name">' + m.name + '</span>' +
        '<span class="sc-fc-tile-meta">' + m.sizes.length + (m.sizes.length === 1 ? ' size' : ' sizes') + '</span>' +
        '</button>';
    });
    html += '<button type="button" class="sc-fc-sofa-tile" data-model="custom">' +
      '<span class="sc-fc-tile-name">Custom / other</span>' +
      '<span class="sc-fc-tile-meta">enter your own</span></button>';
    grid.innerHTML = html;

    grid.addEventListener('click', function (e) {
      var tile = e.target.closest('.sc-fc-sofa-tile');
      if (!tile) return;
      selectModel(tile.getAttribute('data-model'));
    });
  }

  function selectModel(key) {
    STATE.modelKey = key;
    STATE.sizeKey = null;
    grid.querySelectorAll('.sc-fc-sofa-tile').forEach(function (t) {
      t.classList.toggle('is-selected', t.getAttribute('data-model') === key);
    });

    var m = getModel(key);
    if (m) {
      STATE.legsRemovable = m.legsRemovable;
      STATE.split = m.split;
      renderSizeChips(m);
      sizeRow.classList.add('is-active');
      if (dimsBox) dimsBox.style.display = 'none';
    } else {
      /* Custom: no size chips, straight to a blank dims editor.
         Flags start unticked — conservative until the customer says. */
      STATE.legsRemovable = false;
      STATE.split = false;
      sizeRow.classList.remove('is-active');
      STATE.dims = { w: null, h: null, d: null, access: null, legH: null };
      renderDims(null);
      if (dimsBox) dimsBox.style.display = 'block';
    }
    validateStep1();
  }

  function renderSizeChips(m) {
    var html = '';
    m.sizes.forEach(function (s) {
      html += '<button type="button" class="sc-fc-size-chip" data-size="' + s.key + '">' + s.label + '</button>';
    });
    html += '<button type="button" class="sc-fc-size-chip" data-size="bespoke">Bespoke size</button>';
    sizeChips.innerHTML = html;
    sizeChips.onclick = function (e) {
      var chip = e.target.closest('.sc-fc-size-chip');
      if (!chip) return;
      selectSize(chip.getAttribute('data-size'));
    };
  }

  function selectSize(sizeKey) {
    STATE.sizeKey = sizeKey;
    sizeChips.querySelectorAll('.sc-fc-size-chip').forEach(function (c) {
      c.classList.toggle('is-selected', c.getAttribute('data-size') === sizeKey);
    });
    var m = getModel(STATE.modelKey);
    var s = null;
    if (m && sizeKey !== 'bespoke') {
      for (var i = 0; i < m.sizes.length; i++) if (m.sizes[i].key === sizeKey) s = m.sizes[i];
    }
    if (s) {
      STATE.dims = { w: s.w, h: s.h, d: s.d, access: s.access, legH: m.legH };
    } else if (m) {
      /* Bespoke: keep the model's frame numbers, blank the width. */
      var ref = m.sizes[Math.floor(m.sizes.length / 2)];
      STATE.dims = { w: null, h: ref.h, d: ref.d, access: ref.access, legH: m.legH };
    }
    renderDims(m);
    if (dimsBox) dimsBox.style.display = 'block';
    validateStep1();
  }

  function renderDims(m) {
    if (!dimsBox) return;
    var u = UNIT;
    var d = STATE.dims;
    function field(label, key, val, hint) {
      return '<div class="sc-fc-field"><label>' + label + (hint ? ' <span class="sc-fc-field-hint">' + hint + '</span>' : '') + '</label>' +
        '<input type="number" inputmode="decimal" min="0" step="0.5" data-dim="' + key + '" value="' + (val == null ? '' : val) + '">' +
        '<span class="suffix">' + u + '</span></div>';
    }
    var legsNote = '';
    var splitNote = '';
    if (m) {
      if (!m.legsRemovable) legsNote = '<p class="sc-fc-flag-note">The ' + m.name + ' does not usually have removable legs, but get in touch and we may still be able to accommodate.</p>';
      if (!m.split) splitNote = '<p class="sc-fc-flag-note">The ' + m.name + ' is not usually available with a split frame, but get in touch and we may still be able to accommodate.</p>';
    }
    dimsBox.innerHTML =
      '<div class="sc-fc-dims-head">Your sofa\u2019s measurements <span class="sc-fc-dims-sub">' +
      (m ? 'Pre-filled from our standard specs. Ordering bespoke? Adjust any of them.' : 'Enter what you know. The more you give us, the better the answer.') +
      '</span></div>' +
      '<div class="sc-fc-dims-grid">' +
      field('External width', 'w', d.w) +
      field('External height', 'h', d.h) +
      field('External depth', 'd', d.d) +
      field('Access height', 'access', d.access, 'height with removable parts off') +
      field('Leg height', 'legH', d.legH) +
      '</div>' +
      '<div class="sc-fc-flags">' +
      '<label class="sc-fc-flag"><input type="checkbox" data-flag="legs"' + (STATE.legsRemovable ? ' checked' : '') + '> Legs can be removed</label>' +
      '<label class="sc-fc-flag"><input type="checkbox" data-flag="split"' + (STATE.split ? ' checked' : '') + '> Split frame available</label>' +
      '</div>' +
      legsNote + splitNote +
      '<p class="sc-fc-flag-note">Split frames are only available on loose cover builds.</p>';

    dimsBox.querySelectorAll('input[data-dim]').forEach(function (inp) {
      inp.addEventListener('input', function () {
        var v = parseFloat(inp.value);
        STATE.dims[inp.getAttribute('data-dim')] = isNaN(v) ? null : v;
        validateStep1();
      });
    });
    dimsBox.querySelectorAll('input[data-flag]').forEach(function (inp) {
      inp.addEventListener('change', function () {
        if (inp.getAttribute('data-flag') === 'legs') STATE.legsRemovable = inp.checked;
        else STATE.split = inp.checked;
      });
    });
  }

  function validateStep1() {
    var d = STATE.dims;
    var ok = STATE.modelKey && d.w > 0 && d.d > 0 && (d.access > 0 || d.h > 0);
    if (toStep2) toStep2.disabled = !ok;
  }

  /* ============ 4. STEP 2 — presets, units, toggles ============ */
  function applyMarketPresets() {
    ['front', 'hall', 'stair', 'room'].forEach(function (key) {
      var sel = document.querySelector('select[data-preset="' + key + '"]');
      if (!sel) return;
      var opts = MARKET.doorPresets[key];
      var html = '<option value="">\u2014 Choose \u2014</option>';
      opts.forEach(function (o) { html += '<option value="' + o[0] + '">' + o[1] + '</option>'; });
      html += '<option value="custom">I\u2019ll measure it myself</option>';
      sel.innerHTML = html;
    });
    document.querySelectorAll('[data-suffix]').forEach(function (s) { s.textContent = STATE.inputUnit; });
  }

  function readInput(key) {
    var i = document.querySelector('input[data-key="' + key + '"]');
    if (!i || i.value === '') return null;
    var v = parseFloat(i.value);
    if (isNaN(v) || v < 0) return null;
    /* Convert typed units to the market's native unit. */
    if (STATE.inputUnit !== UNIT) {
      v = (STATE.inputUnit === 'in') ? v * 2.54 : v / 2.54;
    }
    return v;
  }

  function wirePresets() {
    document.querySelectorAll('select[data-preset]').forEach(function (sel) {
      sel.addEventListener('change', function () {
        var key = sel.getAttribute('data-preset');
        var custom = document.querySelector('[data-custom-for="' + key + '"]');
        if (sel.value === 'custom') {
          if (custom) custom.style.display = '';
          return;
        }
        if (custom) custom.style.display = 'none';
        var parts = sel.value === '' ? [] : sel.value.split(',');
        var keys = { front: ['frontW', 'frontH'], hall: ['hallW', 'hallH'], stair: ['stairW', 'stairH', 'landingW'], room: ['roomW', 'roomH'] }[key];
        keys.forEach(function (k, idx) {
          var i = document.querySelector('input[data-key="' + k + '"]');
          if (i) i.value = parts[idx] != null ? parts[idx] : '';
        });
      });
    });
  }

  function wireUnitToggle() {
    document.querySelectorAll('.sc-fc-unit-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var u = btn.getAttribute('data-unit');
        if (u === STATE.inputUnit) return;
        /* Convert already-typed values in place. */
        document.querySelectorAll('input[data-key]').forEach(function (i) {
          if (i.value === '') return;
          var v = parseFloat(i.value);
          if (isNaN(v)) return;
          i.value = (u === 'in' ? v / 2.54 : v * 2.54).toFixed(1).replace(/\.0$/, '');
        });
        STATE.inputUnit = u;
        document.querySelectorAll('.sc-fc-unit-btn').forEach(function (b) { b.classList.toggle('is-active', b === btn); });
        document.querySelectorAll('[data-suffix]').forEach(function (s) { s.textContent = u; });
      });
    });
    /* Default the toggle to the market's native unit. */
    document.querySelectorAll('.sc-fc-unit-btn').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-unit') === UNIT);
    });
    STATE.inputUnit = UNIT;
  }

  function wireToggles() {
    var st = document.getElementById('sc-fc-toggle-stairs');
    if (st) st.addEventListener('change', function () {
      STATE.stairs = st.checked;
      var f = document.getElementById('sc-fc-stairs-fields');
      if (f) f.style.display = st.checked ? '' : 'none';
    });
    var co = document.getElementById('sc-fc-toggle-corner');
    if (co) co.addEventListener('change', function () {
      STATE.corner = co.checked;
      var f = document.getElementById('sc-fc-corner-fields');
      if (f) f.style.display = co.checked ? '' : 'none';
    });
  }

  /* ============ 5. THE ENGINE ============ */
  var TIER = { fit: 0, tight: 1, onend: 2, team: 3 };
  function worse(a, b) { return TIER[a] >= TIER[b] ? a : b; }
  function fmt(v) { return (Math.round(v * 10) / 10) + '\u00A0' + UNIT; }

  /* Corner sweep: longest length that survives every angle of a
     90-degree turn between corridors a and b, carrying thickness t. */
  function cornerLimit(a, b, t) {
    var min = Infinity;
    for (var deg = 5; deg <= 85; deg += 0.5) {
      var th = deg * Math.PI / 180;
      var L = a / Math.sin(th) + b / Math.cos(th) - t / (Math.sin(th) * Math.cos(th));
      if (L < min) min = L;
    }
    return min;
  }

  /* One opening (door): returns { tier, lines: [..] } */
  function checkOpening(label, w, h) {
    var d = STATE.dims;
    var acc = effectiveAccess();
    var t = Math.min(d.d, acc);        /* what passes through the width */
    var big = Math.max(d.d, acc);      /* what must clear the height    */
    var lines = [];

    if (w == null) return null;        /* stop not measured — skip */
    if (w === 0) return { tier: 'fit', lines: [{ tier: 'fit', text: label + ': open plan, nothing to squeeze through.' }] };

    /* A — straight side-carry */
    var spareW = w - t;
    var hOk = (h == null) || (h - big >= 0);
    if (spareW >= MARGIN && hOk && (h == null || h - big >= MARGIN)) {
      lines.push({ tier: 'fit', text: label + ': fits with ' + fmt(spareW) + ' to spare, carried on its side.' });
      return { tier: 'fit', lines: lines };
    }
    if (spareW >= 0 && hOk) {
      lines.push({ tier: 'tight', text: label + ': goes through on its side, but with only ' + fmt(spareW) + ' spare. Because every frame is handmade, sizes can vary slightly, so please tell the team about this opening before we arrange delivery.' });
      return { tier: 'tight', lines: lines };
    }

    /* B — on its end, walked straight through */
    if (h != null) {
      if (t <= w && d.w <= h - MARGIN) {
        lines.push({ tier: 'onend', text: label + ': too big for a flat carry, but from what you\u2019ve told us it is likely to fit carried on its end. Contact the team to verify before we arrange delivery.' });
        return { tier: 'onend', lines: lines };
      }
      if (t <= w && d.w <= h) {
        lines.push({ tier: 'onend', text: label + ': carried on its end it is within ' + fmt(h - d.w) + ' of the frame. That is very tight, so contact the team to verify before we arrange delivery.' });
        return { tier: 'onend', lines: lines };
      }

      /* C — lean-through: closed-form minimums.
         Feasible when the sofa\u2019s thin side beats the door height:
         r* = W * t / h  and  headroom c* at the steepest usable angle. */
      if (t <= w && t < h) {
        var cosMax = t / h;                       /* cos(theta*) */
        var sinMax = Math.sqrt(1 - cosMax * cosMax);
        var rStar = d.w * cosMax;
        var cStar = d.w * sinMax + t * cosMax;
        lines.push({ tier: 'onend', text: label + ': taller than the opening even on its end, but it may still lean through. That would need roughly ' + fmt(rStar) + ' of clear run beyond the door and about ' + fmt(cStar) + ' of headroom on the far side. This is one for the team to verify with you before we arrange delivery.' });
        return { tier: 'onend', lines: lines };
      }
    }

    lines.push({ tier: 'team', text: label + ': from these measurements it will not go through this opening in one piece by the usual carries.' });
    return { tier: 'team', lines: lines };
  }

  /* Hallway: straight width, plus the corner if declared. */
  function checkHall(w, ceil, w2) {
    var d = STATE.dims;
    var acc = effectiveAccess();
    var t = Math.min(d.d, acc);
    var lines = [];
    var tier = 'fit';

    if (w == null) return null;
    if (w === 0) return { tier: 'fit', lines: [{ tier: 'fit', text: 'Hallway: none to worry about.' }] };

    var spare = w - t;
    if (spare < 0) {
      return { tier: 'team', lines: [{ tier: 'team', text: 'Hallway: at ' + fmt(w) + ' wide it is narrower than the sofa\u2019s slimmest carrying side (' + fmt(t) + ').' }] };
    }
    if (spare < MARGIN) {
      tier = 'tight';
      lines.push({ tier: 'tight', text: 'Hallway: passable along its length, but with only ' + fmt(spare) + ' spare. Please flag this to the team.' });
    } else {
      lines.push({ tier: 'fit', text: 'Hallway: comfortable along its length, ' + fmt(spare) + ' to spare.' });
    }

    if (STATE.corner && w2 != null && w2 > 0) {
      /* Flat carry around the corner first. */
      var flatLimit = cornerLimit(w, w2, t);
      if (d.w <= flatLimit - MARGIN) {
        lines.push({ tier: 'fit', text: 'The corner: turns flat with room to spare.' });
      } else if (d.w <= flatLimit) {
        tier = worse(tier, 'tight');
        lines.push({ tier: 'tight', text: 'The corner: turns flat, but only just (within ' + fmt(flatLimit - d.w) + ' of the limit). Please flag it to the team.' });
      } else {
        /* On its end: the footprint shrinks to depth by access height. */
        var footLong = Math.max(d.d, acc);
        var footThin = Math.min(d.d, acc);
        var endLimit = cornerLimit(w, w2, footThin);
        if (footLong <= endLimit - MARGIN) {
          if (ceil != null && ceil >= d.w + MARGIN) {
            tier = worse(tier, 'onend');
            lines.push({ tier: 'onend', text: 'The corner: too tight flat, but from what you\u2019ve told us it is likely the sofa can be walked round on its end, and your ' + fmt(ceil) + ' ceiling gives the headroom to stand it up. Contact the team to verify before we arrange delivery.' });
          } else if (ceil != null) {
            tier = worse(tier, 'team');
            lines.push({ tier: 'team', text: 'The corner: it could turn on its end, but standing the sofa up needs at least ' + fmt(d.w + MARGIN) + ' of ceiling and you\u2019ve measured ' + fmt(ceil) + '. Talk to the team, there may be another route.' });
          } else {
            tier = worse(tier, 'onend');
            lines.push({ tier: 'onend', text: 'The corner: too tight flat, but likely possible with the sofa on its end, provided the ceiling at the corner is at least ' + fmt(d.w + MARGIN) + '. Measure that spot and contact the team to verify before we arrange delivery.' });
          }
        } else {
          tier = worse(tier, 'team');
          lines.push({ tier: 'team', text: 'The corner: from these measurements it will not turn, even on its end.' });
        }
      }
    }

    return { tier: tier, lines: lines };
  }

  /* Stairs: flat carry only — a sofa cannot stand on a slope, and
     headroom over treads is nearly always less than it looks. */
  function checkStairs(w, ceil, landing) {
    var d = STATE.dims;
    var acc = effectiveAccess();
    var t = Math.min(d.d, acc);
    var lines = [];
    var tier = 'fit';

    if (w == null) return null;

    var spare = w - t;
    if (spare < 0) {
      return { tier: 'team', lines: [{ tier: 'team', text: 'Stairs: the flight is narrower than the sofa\u2019s slimmest carrying side.' }] };
    }
    if (spare < MARGIN) { tier = 'tight'; lines.push({ tier: 'tight', text: 'Stairs: up the flight with only ' + fmt(spare) + ' spare. Flag it to the team.' }); }
    else lines.push({ tier: 'fit', text: 'Stairs: the flight itself is fine, ' + fmt(spare) + ' to spare.' });

    if (ceil != null) {
      var swing = Math.sqrt(d.d * d.d + acc * acc);
      if (swing > ceil) {
        tier = worse(tier, 'team');
        lines.push({ tier: 'team', text: 'Stairs: manoeuvring on the flight needs about ' + fmt(swing) + ' of headroom and you have ' + fmt(ceil) + '. Talk to the team.' });
      } else if (swing > ceil - MARGIN) {
        tier = worse(tier, 'tight');
        lines.push({ tier: 'tight', text: 'Stairs: headroom is close (' + fmt(ceil - swing) + ' spare while turning). Flag it to the team.' });
      }
    }

    if (landing != null && landing > 0) {
      var limit = cornerLimit(w, landing, t);
      if (d.w <= limit - MARGIN) {
        lines.push({ tier: 'fit', text: 'The landing turn: fine, carried flat.' });
      } else if (d.w <= limit) {
        tier = worse(tier, 'tight');
        lines.push({ tier: 'tight', text: 'The landing turn: possible but within ' + fmt(limit - d.w) + ' of the limit. Flag it to the team.' });
      } else {
        tier = worse(tier, 'team');
        lines.push({ tier: 'team', text: 'The landing turn: too tight for a flat carry. Landings are judgement territory \u2014 a partial up-end is sometimes possible, but this is one for the delivery team to assess with you.' });
      }
    }

    return { tier: tier, lines: lines };
  }

  /* ============ 6. RESULTS ============ */
  var COPY = {
    fit: {
      badge: 'It fits',
      head: 'Good news \u2014 it goes.',
      body: 'Every opening you\u2019ve measured clears our safety margin. You can order with confidence, and our delivery team will treat it gently anyway.'
    },
    tight: {
      badge: 'Tight squeeze',
      head: 'It should fit \u2014 but it\u2019s close.',
      body: 'At least one point on the route is inside our ' + MARGIN + '\u00A0' + UNIT + ' safety margin. Because every sofa is handmade, there is a little natural variation in the finished frame, so we would like to check your measurements with you before we arrange delivery.'
    },
    onend: {
      badge: 'Likely \u2014 verify with us',
      head: 'Likely to fit, carried on its end.',
      body: 'A flat carry will not go, but from the measurements you\u2019ve given us it is likely the sofa can be brought in on its end \u2014 our delivery teams do this routinely. Please contact the team to verify before we arrange delivery, so there are no surprises on the day.'
    },
    team: {
      badge: 'Talk to us',
      head: 'It might be a squeeze.',
      body: 'From these measurements at least one point on the route is tighter than our standard carries allow. That is not the end of the story: leg removal, split frames and window routes have rescued many a delivery. Talk to the team and we will work through the problem with you.'
    }
  };

  function renderResult() {
    var shell = document.getElementById('sc-fc-result-shell');
    if (!shell) return;

    var contactEmail = (window.SC && SC.fitChecker && SC.fitChecker.contactEmail) || 'sofas@sophieconran.com';

    var allLines = [];   /* { tier, text } for per-line colouring */
    var checks = [];
    var frontW = readInput('frontW'), frontH = readInput('frontH');
    var hallW = readInput('hallW'), hallH = readInput('hallH'), hallW2 = readInput('hallW2');
    var roomW = readInput('roomW'), roomH = readInput('roomH'), roomC = readInput('roomC');

    var front = checkOpening('Front door', frontW, frontH);
    if (front) checks.push(front);
    var hall = checkHall(hallW, hallH, hallW2);
    if (hall) checks.push(hall);
    if (STATE.stairs) {
      var st = checkStairs(readInput('stairW'), readInput('stairH'), readInput('landingW'));
      if (st) checks.push(st);
    }
    var room = checkOpening('Room door', roomW, roomH);
    if (room) checks.push(room);
    if (room && room.tier === 'onend' && roomC != null && roomC < STATE.dims.w + MARGIN) {
      room.tier = 'team';
      room.lines.push({ tier: 'team', text: 'Note: your ' + fmt(roomC) + ' room ceiling is under the ' + fmt(STATE.dims.w + MARGIN) + ' needed to stand the sofa up on the far side, so talk to the team about this door.' });
    }

    if (!checks.length) {
      shell.innerHTML = '<div class="sc-fc-result sc-fc-result-team"><h3>Nothing to check yet</h3><p>Pop at least one measurement into step two and we will give you an honest answer.</p></div>';
      return;
    }

    var overall = 'fit';
    checks.forEach(function (c) {
      overall = worse(overall, c.tier);
      c.lines.forEach(function (l) { allLines.push(l); });
    });
    var copy = COPY[overall];

    var detail = '';
    allLines.forEach(function (l) {
      detail += '<li class="sc-fc-line sc-fc-line-' + l.tier + '">' + l.text + '</li>';
    });

    var extras = '';
    if (overall === 'onend' || overall === 'team') {
      if (STATE.split) {
        extras += '<p class="sc-fc-extra">A split frame \u2014 the sofa arrives in two halves and is joined in the room \u2014 often unlocks routes like this. Split frames are only available on loose cover builds; mention it when you get in touch.</p>';
      } else {
        var m = getModel(STATE.modelKey);
        extras += '<p class="sc-fc-extra">' + (m ? 'The ' + m.name : 'This model') + ' is not usually available with a split frame, but get in touch and we may still be able to accommodate.</p>';
      }
    }

    shell.innerHTML =
      '<div class="sc-fc-result sc-fc-result-' + overall + '">' +
      '<span class="sc-fc-badge">' + copy.badge + '</span>' +
      '<h3>' + copy.head + '</h3>' +
      '<p>' + copy.body + '</p>' +
      '<ul class="sc-fc-detail">' + detail + '</ul>' +
      extras +
      '<div class="sc-fc-result-ctas">' +
      '<a class="sc-btn sc-btn-primary" href="mailto:' + contactEmail + '?subject=Access%20check%20\u2014%20' + encodeURIComponent((getModel(STATE.modelKey) || { name: 'Custom sofa' }).name) + '">Email the team</a>' +
      '<a class="sc-btn sc-btn-text" href="#" data-sc-nadia-open>Or talk to Nadia now</a>' +
      '</div>' +
      '</div>';
  }

  /* ============ 7. STEPPER / NAV ============ */
  function goToStep(n) {
    document.querySelectorAll('.sc-fc-step').forEach(function (s) {
      s.classList.toggle('is-active', s.getAttribute('data-step') === String(n));
    });
    document.querySelectorAll('.sc-fc-panel').forEach(function (p) {
      p.classList.toggle('is-active', p.getAttribute('data-panel') === String(n));
    });
    var card = document.getElementById('sc-fc-card');
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function wireNav() {
    if (toStep2) toStep2.addEventListener('click', function () { goToStep(2); });
    var to3 = document.getElementById('sc-fc-to-3');
    if (to3) to3.addEventListener('click', function () { renderResult(); goToStep(3); });
    document.querySelectorAll('[data-back]').forEach(function (b) {
      b.addEventListener('click', function () { goToStep(b.getAttribute('data-back')); });
    });
    var reset = document.getElementById('sc-fc-reset');
    if (reset) reset.addEventListener('click', function () {
      STATE.modelKey = null; STATE.sizeKey = null;
      STATE.dims = { w: null, h: null, d: null, access: null, legH: null };
      document.querySelectorAll('.sc-fc-sofa-tile, .sc-fc-size-chip').forEach(function (el) { el.classList.remove('is-selected'); });
      document.querySelectorAll('input[data-key]').forEach(function (i) { i.value = ''; });
      document.querySelectorAll('select[data-preset]').forEach(function (s) { s.value = ''; });
      if (dimsBox) if (dimsBox) dimsBox.style.display = 'none';
      if (sizeRow) sizeRow.classList.remove('is-active');
      validateStep1();
      goToStep(1);
    });
    /* Allow clicking completed steps in the stepper. */
    document.querySelectorAll('.sc-fc-step').forEach(function (s) {
      s.addEventListener('click', function () { goToStep(s.getAttribute('data-step')); });
    });
  }

  /* ============ INIT ============ */
  document.addEventListener('DOMContentLoaded', function () {
    if (!document.getElementById('sc-fc-card')) return;
    renderGrid();
    applyMarketPresets();
    wirePresets();
    wireUnitToggle();
    wireToggles();
    wireNav();
    validateStep1();
  });
})();
